import { PLUNDER } from '@somnhold/content';
import { dist2, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, PlunderCache } from '../types.ts';
import { targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { plunderCanLeave, plunderCarry, plunderLeave } from '../battle/plunder.ts';
import { castSkill } from '../battle/skills.ts';
import { clericCare, useSkills } from './ghost.ts';

/*
 * A plunder as a capable player runs it (balance runs only): fell what is close, run down a dragger hauling a
 * cache, carry the nearest free cache to the cart (a coffer with a partner), and head home once the cart holds
 * `botLeave` (everything left on the field, if unset).
 */

const standing = (h: Hero): boolean => h.downTicks === 0;
const CART = { x: 0, y: 0 };
/** Nightmares this close are worth turning on, hands empty. */
const FIGHT_RANGE = 2600;
/** A nightmare this close to a carrier: time to put the load down. */
const CAUGHT = 1400;

const go = (s: BattleState, h: Hero, p: { x: number; y: number }, near = 300): boolean => {
  if (withinRange(h.x, h.y, p.x, p.y, near)) {
    if (h.moving && withinRange(h.x, h.y, p.x, p.y, 200)) h.moving = false;
    return true;
  }
  if (!h.moving || Math.abs(h.destX - p.x) + Math.abs(h.destY - p.y) > 300) orderMove(s, h, p.x, p.y);
  return false;
};

const nearestOf = <T extends { x: number; y: number }>(h: Hero, xs: T[]): T | undefined => {
  let best: T | undefined;
  let bd = 0x7fffffff;
  for (const o of xs) {
    const d = dist2(h.x, h.y, o.x, o.y);
    if (d < bd) {
      bd = d;
      best = o;
    }
  }
  return best;
};

const fight = (s: BattleState, h: Hero, target: Enemy, crowd: number): void => {
  useSkills(s, h, target);
  if (h.cls !== 'cleric' && h.skillCd[2] === 0 && crowd >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: target.x, y: target.y, id: target.id });
  if (h.target !== target.id) {
    h.target = target.id;
    h.forced = true;
    h.moving = false;
    h.path = [];
  }
};

export const plunderBot = (s: BattleState, h: Hero): void => {
  const p = s.plunder;
  if (!p || p.end || !standing(h) || h.stun > 0) return;
  if (h.cls === 'cleric' && h.skillCd[2] === 0 && s.heroes.some((o) => o.downTicks > 0)) castSkill(s, h, { t: 'skill', slot: 2 });
  const held = p.caches.find((c) => c.carriers.includes(h.id));
  // Caught with full hands: put it down and fight (a dragger may try for it meanwhile).
  if (held && s.enemies.some((e) => e.hp > 0 && e.kind !== 'dragger' && e.kind !== 'boneWall' && withinRange(h.x, h.y, e.x, e.y, CAUGHT))) {
    plunderCarry(s, h);
    return;
  }
  if (held) {
    // A coffer waits for its partner.
    if (held.kind === 'coffer' && held.carriers.length < 2) {
      h.moving = false;
      return;
    }
    go(s, h, CART, 600);
    return;
  }
  const goal = p.botLeave ?? 0x7fffffff;
  const home = p.loaded >= goal || p.caches.length === 0;
  if (home) {
    if (go(s, h, CART, PLUNDER.leaveRange - 600) && plunderCanLeave(s)) plunderLeave(s, h);
    return;
  }
  const foes = s.enemies.filter((e) => e.hp > 0 && targetable(s, e) && e.kind !== 'boneWall');
  // A dragger hauling a cache first: the cache is gone if it reaches its hole.
  const thief = nearestOf(h, foes.filter((e) => p.caches.some((c) => c.dragger === e.id)));
  const close = foes.filter((e) => withinRange(h.x, h.y, e.x, e.y, FIGHT_RANGE));
  const target = thief ?? nearestOf(h, close);
  if (target) {
    fight(s, h, target, close.length);
    return;
  }
  if (h.target) h.target = 0;
  // A coffer someone already holds needs a second pair of hands; else the nearest free cache.
  const live = s.heroes.filter((o) => standing(o) && o.control !== 'ghost').length;
  const half = p.caches.find((c) => c.kind === 'coffer' && c.carriers.length === 1);
  const free = p.caches.filter((c): c is PlunderCache => c.carriers.length === 0 && !c.dragger && (c.kind === 'chest' || live >= 2));
  const next = half ?? nearestOf(h, free);
  if (!next) {
    if (h.cls === 'cleric') clericCare(s, h);
    go(s, h, CART, 1500);
    return;
  }
  if (go(s, h, next, PLUNDER.reach - 300)) plunderCarry(s, h);
};
